// src/pages/Consultation/tabs/ExchangeTab.jsx - UPDATED WITH COMPLETE CRUD
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  FiPlus,
  FiDollarSign,
  FiEdit,
  FiCheck,
  FiX,
  FiImage,
  FiAlertCircle,
  FiInfo,
  FiTrash2,
  FiRotateCcw,
  FiEdit3,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "../../../../components/common/Button";
import {
  adjustExchangeValueThunk,
  acceptExchangeItemThunk,
  editExchangeItemThunk,
  removeExchangeItemThunk,
  unacceptExchangeItemThunk,
  calculatePricingThunk,
} from "../../../../store/thunks/consultationThunks";

const ExchangeTab = ({ session, adminInfo, onAddExchangeItem }) => {
  const dispatch = useDispatch();

  const [adjustingItemId, setAdjustingItemId] = useState(null);
  const [editingItemId, setEditingItemId] = useState(null);
  const [adjustmentValue, setAdjustmentValue] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [editFormData, setEditFormData] = useState({});
  const [processingAction, setProcessingAction] = useState(false);

  const exchangeItems = session?.exchangeItems || [];
  const canModify = session?.status === "ACTIVE";

  // Calculate total exchange value
  const calculateTotalExchangeValue = () => {
    return exchangeItems
      .filter((item) => item.customerAccepted)
      .reduce((sum, item) => sum + (item.agreedValue || 0), 0);
  };

  // Handle value adjustment
  const handleAdjustValue = async (itemId) => {
    if (!adjustmentValue || adjustmentValue <= 0) {
      toast.error("Please enter a valid value");
      return;
    }

    if (!adjustmentReason.trim()) {
      toast.error("Please provide a reason for adjustment");
      return;
    }

    setProcessingAction(true);

    try {
      await dispatch(
        adjustExchangeValueThunk({
          sessionId: session._id,
          exchangeItemId: itemId,
          adjustmentData: {
            adjustedValue: parseFloat(adjustmentValue),
            reason: adjustmentReason,
          },
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Exchange value adjusted");
      setAdjustingItemId(null);
      setAdjustmentValue("");
      setAdjustmentReason("");

      // Recalculate pricing
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error(error || "Failed to adjust value");
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle edit exchange item
  const handleEditItem = async (itemId) => {
    if (Object.keys(editFormData).length === 0) {
      toast.error("No changes made");
      return;
    }

    setProcessingAction(true);

    try {
      await dispatch(
        editExchangeItemThunk({
          sessionId: session._id,
          exchangeItemId: itemId,
          updateData: {
            ...editFormData,
            reason: "Admin updated item details",
          },
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Exchange item updated");
      setEditingItemId(null);
      setEditFormData({});

      // Recalculate pricing
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error(error || "Failed to update item");
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle remove exchange item
  const handleRemoveItem = async (itemId) => {
    if (
      !window.confirm(
        "Remove this exchange item? This action cannot be undone."
      )
    ) {
      return;
    }

    setProcessingAction(true);

    try {
      await dispatch(
        removeExchangeItemThunk({
          sessionId: session._id,
          exchangeItemId: itemId,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Exchange item removed");
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error(error || "Failed to remove item");
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle unaccept (customer changed mind)
  const handleUnacceptItem = async (itemId) => {
    if (
      !window.confirm(
        "Reset this accepted item? Customer will need to accept it again."
      )
    ) {
      return;
    }

    setProcessingAction(true);

    try {
      await dispatch(
        unacceptExchangeItemThunk({
          sessionId: session._id,
          exchangeItemId: itemId,
          reason: "Customer changed mind",
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Item reset - needs re-acceptance");
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error(error || "Failed to unaccept item");
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle accept exchange item
  const handleAcceptItem = async (itemId) => {
    if (!window.confirm("Accept this exchange item at the agreed value?")) {
      return;
    }

    setProcessingAction(true);

    try {
      await dispatch(
        acceptExchangeItemThunk({
          sessionId: session._id,
          exchangeItemId: itemId,
          adminCode: adminInfo.adminCode,
          adminName: adminInfo.adminName,
        })
      ).unwrap();

      toast.success("Exchange item accepted");
      dispatch(calculatePricingThunk(session._id));
    } catch (error) {
      toast.error(error || "Failed to accept item");
    } finally {
      setProcessingAction(false);
    }
  };

  // Start editing an item
  const startEditing = (item) => {
    setEditingItemId(item.exchangeItemId);
    setEditFormData({
      grossWeight: item.grossWeight,
      netWeight: item.netWeight,
      stoneWeight: item.stoneWeight,
      conditionGrade: item.conditionGrade,
      description: item.description,
    });
  };

  // Start adjusting an item
  const startAdjustment = (item) => {
    setAdjustingItemId(item.exchangeItemId);
    setAdjustmentValue(
      item.manualAdjustment?.adjustedValue ||
        item.systemAppraisal?.finalValue ||
        ""
    );
    setAdjustmentReason(item.manualAdjustment?.adjustmentReason || "");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FiDollarSign className="text-primary" />
          Exchange Items
        </h3>
        {canModify && (
          <Button onClick={onAddExchangeItem}>
            <FiPlus className="mr-2" />
            Add Exchange Item
          </Button>
        )}
      </div>

      {/* Exchange Items List */}
      {exchangeItems.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <FiDollarSign className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            No Exchange Items Yet
          </p>
          <p className="text-gray-500 mb-6">
            Add items that the customer wants to exchange for credit
          </p>
          {canModify && (
            <Button onClick={onAddExchangeItem}>
              <FiPlus className="mr-2" />
              Add First Exchange Item
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {exchangeItems.map((item) => (
            <ExchangeItemCard
              key={item.exchangeItemId}
              item={item}
              session={session}
              isAdjusting={adjustingItemId === item.exchangeItemId}
              isEditing={editingItemId === item.exchangeItemId}
              adjustmentValue={adjustmentValue}
              adjustmentReason={adjustmentReason}
              editFormData={editFormData}
              onAdjustmentValueChange={setAdjustmentValue}
              onAdjustmentReasonChange={setAdjustmentReason}
              onEditFormChange={(field, value) =>
                setEditFormData((prev) => ({ ...prev, [field]: value }))
              }
              onStartAdjustment={() => startAdjustment(item)}
              onStartEditing={() => startEditing(item)}
              onSaveAdjustment={() => handleAdjustValue(item.exchangeItemId)}
              onSaveEdit={() => handleEditItem(item.exchangeItemId)}
              onCancelAdjustment={() => {
                setAdjustingItemId(null);
                setAdjustmentValue("");
                setAdjustmentReason("");
              }}
              onCancelEdit={() => {
                setEditingItemId(null);
                setEditFormData({});
              }}
              onAccept={() => handleAcceptItem(item.exchangeItemId)}
              onUnaccept={() => handleUnacceptItem(item.exchangeItemId)}
              onRemove={() => handleRemoveItem(item.exchangeItemId)}
              processingAction={processingAction}
              canModify={canModify}
            />
          ))}
        </div>
      )}

      {/* Summary */}
      {exchangeItems.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Exchange Summary</h4>
          <div className="space-y-3">
            <div className="flex justify-between text-gray-700">
              <span>Total Items:</span>
              <span className="font-medium">{exchangeItems.length}</span>
            </div>
            <div className="flex justify-between text-gray-700">
              <span>Accepted:</span>
              <span className="font-medium text-green-600">
                {exchangeItems.filter((i) => i.customerAccepted).length}
              </span>
            </div>
            <div className="border-t pt-3">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">Total Value:</span>
                <span className="text-2xl font-bold text-green-600">
                  ₹{calculateTotalExchangeValue().toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Exchange Item Card Component
const ExchangeItemCard = ({
  item,
  session,
  isAdjusting,
  isEditing,
  adjustmentValue,
  adjustmentReason,
  editFormData,
  onAdjustmentValueChange,
  onAdjustmentReasonChange,
  onEditFormChange,
  onStartAdjustment,
  onStartEditing,
  onSaveAdjustment,
  onSaveEdit,
  onCancelAdjustment,
  onCancelEdit,
  onAccept,
  onUnaccept,
  onRemove,
  processingAction,
  canModify,
}) => {
  const getStatusBadge = (status) => {
    const badges = {
      ACCEPTED: "bg-green-100 text-green-800",
      APPRAISED: "bg-blue-100 text-blue-800",
      NEGOTIATED: "bg-purple-100 text-purple-800",
      REJECTED: "bg-red-100 text-red-800",
      PENDING: "bg-yellow-100 text-yellow-800",
    };
    return badges[status] || badges.PENDING;
  };

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-1 text-xs font-medium bg-gray-100 rounded">
                {item.itemType}
              </span>
              <span
                className={`px-2 py-1 text-xs font-medium rounded ${getStatusBadge(
                  item.status
                )}`}
              >
                {item.status}
              </span>
            </div>
            <h4 className="font-semibold">{item.description}</h4>
          </div>

          {/* Action buttons */}
          {canModify && (
            <div className="flex gap-1">
              {!item.customerAccepted ? (
                <>
                  <button
                    onClick={onStartEditing}
                    disabled={processingAction || isAdjusting || isEditing}
                    className="p-1.5 hover:bg-gray-100 rounded text-gray-600"
                    title="Edit details"
                  >
                    <FiEdit3 size={16} />
                  </button>
                  <button
                    onClick={onRemove}
                    disabled={processingAction}
                    className="p-1.5 hover:bg-red-50 rounded text-red-600"
                    title="Remove item"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </>
              ) : (
                <button
                  onClick={onUnaccept}
                  disabled={processingAction}
                  className="p-1.5 hover:bg-orange-50 rounded text-orange-600"
                  title="Reset acceptance"
                >
                  <FiRotateCcw size={16} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Edit Form */}
        {isEditing ? (
          <div className="space-y-3 p-3 bg-gray-50 rounded">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-600">
                  Gross Weight (g)
                </label>
                <input
                  type="number"
                  value={editFormData.grossWeight || ""}
                  onChange={(e) =>
                    onEditFormChange("grossWeight", e.target.value)
                  }
                  className="w-full px-2 py-1 text-sm border rounded"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Net Weight (g)</label>
                <input
                  type="number"
                  value={editFormData.netWeight || ""}
                  onChange={(e) =>
                    onEditFormChange("netWeight", e.target.value)
                  }
                  className="w-full px-2 py-1 text-sm border rounded"
                />
              </div>
              <div>
                <label className="text-xs text-gray-600">Condition</label>
                <select
                  value={editFormData.conditionGrade || ""}
                  onChange={(e) =>
                    onEditFormChange("conditionGrade", e.target.value)
                  }
                  className="w-full px-2 py-1 text-sm border rounded"
                >
                  <option value="EXCELLENT">Excellent</option>
                  <option value="GOOD">Good</option>
                  <option value="FAIR">Fair</option>
                  <option value="POOR">Poor</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={onSaveEdit} loading={processingAction}>
                Save
              </Button>
              <Button size="sm" variant="outline" onClick={onCancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Display Details */}
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div>
                <p className="text-xs text-gray-500">Net Weight</p>
                <p className="font-medium">{item.netWeight}g</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Purity</p>
                <p className="font-medium">
                  {item.metalDetails?.purity || "-"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Condition</p>
                <p className="font-medium">{item.conditionGrade}</p>
              </div>
            </div>

            {/* Value Section */}
            {isAdjusting ? (
              <div className="p-3 bg-gray-50 rounded space-y-2">
                <input
                  type="number"
                  value={adjustmentValue}
                  onChange={(e) => onAdjustmentValueChange(e.target.value)}
                  placeholder="New value"
                  className="w-full px-2 py-1 text-sm border rounded"
                />
                <textarea
                  value={adjustmentReason}
                  onChange={(e) => onAdjustmentReasonChange(e.target.value)}
                  placeholder="Reason..."
                  rows={2}
                  className="w-full px-2 py-1 text-sm border rounded resize-none"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={onSaveAdjustment}
                    loading={processingAction}
                  >
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onCancelAdjustment}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="p-3 bg-green-50 rounded">
                <p className="text-xs text-gray-600 mb-1">
                  {item.customerAccepted ? "Agreed Value" : "Current Value"}
                </p>
                <p className="text-xl font-bold text-green-700">
                  ₹
                  {(
                    item.agreedValue ||
                    item.systemAppraisal?.finalValue ||
                    0
                  ).toLocaleString()}
                </p>
              </div>
            )}
          </>
        )}

        {/* Actions */}
        {canModify && !item.customerAccepted && !isEditing && !isAdjusting && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={onStartAdjustment}
              disabled={processingAction}
            >
              <FiEdit size={14} className="mr-1" />
              Adjust Value
            </Button>
            <Button
              size="sm"
              onClick={onAccept}
              disabled={processingAction}
              className="bg-green-600 hover:bg-green-700"
            >
              <FiCheck size={14} className="mr-1" />
              Accept
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExchangeTab;
